import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import { AUTH_FIRM_SELECTOR_SEARCH_THRESHOLD } from 'common/constants/auth';
import { FIRM_LIST_STATUSES } from 'common/types/firm';
import { HiddenContentChip } from 'components';
import FeatureEnabler from 'components/FeatureEnabler';
import { LoadingState, Stack } from 'components/ui';
import { getZoomAdjustedCssValue, getZoomAdjustedPx } from 'components/ui/utilities/zoom';
import { useZoom } from 'context/ZoomContext';
import { FirmStepper } from 'layouts/Main/components';
import AddFirmAction from 'pages/Auth/components/AddFirmAction';
import ContactAdminOrInquireLicensing from 'pages/Auth/components/ContactAdminOrInquireLicensing';
import FirmList from 'pages/Auth/components/FirmList';
import ListHeader from 'pages/Auth/components/ListHeader';
import { LIST_HEADER_SEARCH_FIRMS_LABEL } from 'pages/Auth/components/ListHeader/ListHeader.constants';
import SignOutButton from 'pages/Auth/components/SignOutButton';
import { useAuthInitialFocus, useAuthSelectorKeyboardNavigation } from 'pages/Auth/hooks';
import { FIRM_SELECTOR_LOADING_LABEL } from './FirmSelector.constants';
import { FirmSelectorProps } from './FirmSelector.props';
import { FirmListContainer, RootPaper } from './FirmSelector.styles';

const FirmSelector = ({
  firmList,
  firmListStatus,
  isLoading,
  user,
}: FirmSelectorProps) => {
  const firmListRef = useRef<HTMLUListElement>(null);
  const inputFilterRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [hiddenBottom, setHiddenBottom] = useState(0);
  const [openStepper, setOpenStepper] = useState(false);
  const { zoomLevel } = useZoom();
  const shouldShowSearch = (firmList?.length ?? 0) > AUTH_FIRM_SELECTOR_SEARCH_THRESHOLD;
  const showAddFirmOnly = !isLoading && isEmpty(firmList) && Boolean(user?.is_superuser);
  const showFirmAccessFallback = !isLoading
    && !showAddFirmOnly
    && (!firmList || firmListStatus === FIRM_LIST_STATUSES.FORBIDDEN || firmList.length === 0);
  const { focusFirstOption, handleOptionsKeyDown, handleSearchKeyDown } = useAuthSelectorKeyboardNavigation({
    inputRef: inputFilterRef,
    listRef: firmListRef,
  });

  const updateHiddenFirms = useCallback(() => {
    const firmListContainer = firmListRef.current;

    if (!firmListContainer) {
      return;
    }

    const firmListItems = firmListContainer.querySelectorAll('a');
    let nextHiddenBottom = 0;

    firmListItems.forEach(firm => {
      if (firm.offsetTop >= firmListContainer.offsetHeight + firmListContainer.scrollTop) {
        nextHiddenBottom += 1;
      }
    });

    setHiddenBottom(currentHiddenBottom =>
      currentHiddenBottom === nextHiddenBottom ? currentHiddenBottom : nextHiddenBottom
    );
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    updateHiddenFirms();
  }, [firmList, isLoading, search, updateHiddenFirms]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value || '');
  };

  const focusSearchInput = useCallback(() => {
    inputFilterRef.current?.focus();
  }, []);

  useAuthInitialFocus(focusSearchInput, !isLoading && shouldShowSearch);
  useAuthInitialFocus(focusFirstOption, !isLoading && !shouldShowSearch && Boolean(firmList?.length));

  let content;

  if (isLoading) {
    content = <LoadingState label={FIRM_SELECTOR_LOADING_LABEL} minHeight={getZoomAdjustedPx(220, zoomLevel)} />;
  } else if (showAddFirmOnly) {
    content = (
      <Stack alignItems="center" spacing={2} width="100%">
        <AddFirmAction onClickHandler={() => setOpenStepper(true)} />
        {openStepper ? <FirmStepper isVisible={openStepper} onClose={() => setOpenStepper(false)} /> : null}
      </Stack>
    );
  } else if (showFirmAccessFallback) {
    content = (
      <FeatureEnabler enabler="createFirm" alternativeComponent={ContactAdminOrInquireLicensing}>
        <Stack alignItems="center" spacing={2} width="100%">
          <AddFirmAction onClickHandler={() => setOpenStepper(true)} />
          <SignOutButton />
          {openStepper ? <FirmStepper isVisible={openStepper} onClose={() => setOpenStepper(false)} /> : null}
        </Stack>
      </FeatureEnabler>
    );
  } else {
    content = (
      <>
        <ListHeader
          inputId="firm-selector-search"
          inputFilterRef={inputFilterRef}
          itemCount={firmList?.length ?? 0}
          listLimit={AUTH_FIRM_SELECTOR_SEARCH_THRESHOLD}
          onInputKeyDown={handleSearchKeyDown}
          searchLabel={LIST_HEADER_SEARCH_FIRMS_LABEL}
          searchState={search}
          onInputChange={handleSearchChange}
        />
        <FirmListContainer
          id="firm-list"
          onKeyDown={handleOptionsKeyDown}
          ref={firmListRef}
          onScroll={updateHiddenFirms}
          sx={{
            maxHeight: `min(${getZoomAdjustedCssValue('58vh', zoomLevel)}, ${getZoomAdjustedPx(480, zoomLevel)})`,
          }}
        >
          <FirmList
            firmList={firmList}
            searchState={search}
          />
          {openStepper ? <FirmStepper isVisible={openStepper} onClose={() => setOpenStepper(false)} /> : null}
        </FirmListContainer>
        <HiddenContentChip
          id="hidden-firms"
          isVisible={hiddenBottom > 0}
          label={`${hiddenBottom} firms`}
          position="bottom"
        />
      </>
    );
  }

  return <RootPaper>{content}</RootPaper>;
};

export default FirmSelector;
